/**
 * Geometry of the utility blocks (game/utility/defs.ts) for models.ts: every
 * quad of a block id in block space. DOM-free (mesh worker, HUD icons).
 */
import { BLOCKS, ENTITY_TILE_BASE, T } from '../../blocks';
import { CHEST_MODELS, ENDER_CHEST_MODEL, chestQuads, orientedQuad, shulkerBoxQuads, signQuads, type Quad } from '../../entityModels';
import { boxQuads } from '../../models';
import { CH_FACING, CH_KIND, CH_OPEN, CH_TYPE, TUB, UK, U_A, U_B, U_KIND, attachedBox } from './defs';

/** Turn quads about the vertical axis through the cell centre; a front that faced +Z ends up facing (sin a, cos a). */
function turn(quads: Quad[], a: number): Quad[] {
  if (a === 0) return quads;
  const c = Math.cos(a), s = Math.sin(a);
  const FACE_N = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
  return quads.map((q) => {
    const pts: number[][] = [];
    for (let i = 0; i < 4; i++) {
      const x = q.pos[i * 3] - 0.5, z = q.pos[i * 3 + 2] - 0.5;
      pts.push([x * c + z * s + 0.5, q.pos[i * 3 + 1], -x * s + z * c + 0.5]);
    }
    const uvs = [0, 1, 2, 3].map((i) => [q.uv[i * 2], q.uv[i * 2 + 1]]);
    const n = FACE_N[q.face] ?? [0, 1, 0];
    return orientedQuad(pts, uvs, q.tile, [n[0] * c + n[2] * s, n[1], -n[0] * s + n[2] * c]);
  });
}

/** Two crossed upright planes (a potted plant, a campfire's flame), both sides drawn. `lo`..`hi` in 1/16, `w` the half-width. */
function crossQuads(tile: number, lo: number, hi: number, w: number): Quad[] {
  const out: Quad[] = [];
  const y0 = lo / 16, y1 = hi / 16, a = 0.5 - w / 16, b = 0.5 + w / 16;
  for (const [x0, z0, x1, z1] of [[a, a, b, b], [a, b, b, a]]) {
    const pts = [[x0, y0, z0], [x1, y0, z1], [x1, y1, z1], [x0, y1, z0]];
    const uvs = [[0, 1], [1, 1], [1, 0], [0, 0]];
    const n = [z1 - z0, 0, x0 - x1];
    out.push(orientedQuad(pts, uvs, tile, n), orientedQuad(pts, uvs, tile, [-n[0], 0, -n[2]]));
  }
  return out;
}

const all = (t: number): number[] => [t, t, t, t, t, t];
const box = (r: readonly number[], tiles: readonly number[]): Quad[] => boxQuads(r.slice(0, 3), r.slice(3), tiles);
const FACING_ANGLE = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
/** A cuboid whose faces sample explicit texel rects [u0, v0, u1, v1] (a block model's "uv"), per face +X,-X,+Y,-Y,+Z,-Z; a missing rect keeps the default. */
function ubox(r: readonly number[], tiles: readonly number[], uvs: ReadonlyArray<readonly number[] | undefined>): Quad[] {
  return box(r, tiles).map((q, i) => {
    const rect = uvs[i];
    if (!rect) return q;
    const us = [q.uv[0], q.uv[2], q.uv[4], q.uv[6]], vs = [q.uv[1], q.uv[3], q.uv[5], q.uv[7]];
    const u0 = Math.min(...us), u1 = Math.max(...us), v0 = Math.min(...vs), v1 = Math.max(...vs);
    const uv: number[] = [];
    for (let k = 0; k < 4; k++) {
      uv.push((rect[0] + ((us[k] - u0) / (u1 - u0 || 1)) * (rect[2] - rect[0])) / 16, (rect[1] + ((vs[k] - v0) / (v1 - v0 || 1)) * (rect[3] - rect[1])) / 16);
    }
    return { ...q, uv };
  });
}
/** An upright two-sided plane through the cell centre along X, x0..x1 and y0..y1 in 1/16, sampling `rect`. */
function plane(tile: number, x0: number, y0: number, x1: number, y1: number, rect: readonly number[]): Quad[] {
  const pts = [[x0 / 16, y0 / 16, 0.5], [x1 / 16, y0 / 16, 0.5], [x1 / 16, y1 / 16, 0.5], [x0 / 16, y1 / 16, 0.5]];
  const [a, b, c, d] = rect.map((v) => v / 16);
  const uvs = [[a, d], [c, d], [c, b], [a, b]];
  return [orientedQuad(pts, uvs, tile, [0, 0, 1]), orientedQuad(pts, uvs, tile, [0, 0, -1])];
}
const sides = (side: readonly number[], up?: readonly number[], down?: readonly number[]): Array<readonly number[] | undefined> => [side, side, up, down ?? up, side, side];

const shift = (q: Quad, dx: number, dy: number, dz: number): Quad => ({ ...q, pos: q.pos.map((v, i) => v + (i % 3 === 0 ? dx : i % 3 === 1 ? dy : dz)) });
function cross(p: number[][]): number[] {
  const a = [p[1][0] - p[0][0], p[1][1] - p[0][1], p[1][2] - p[0][2]], b = [p[2][0] - p[1][0], p[2][1] - p[1][1], p[2][2] - p[1][2]];
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

const cache = new Map<number, readonly Quad[]>();

/** The quads of a utility block, or null when `id` is not one that has a model. */
export function utilityQuads(id: number): readonly Quad[] | null {
  const kind = U_KIND[id];
  if (kind === UK.NONE) return null;
  let q = cache.get(id);
  if (!q) {
    const made = build(id, kind);
    if (!made) return null;
    cache.set(id, q = made);
  }
  return q;
}

function build(id: number, kind: number): Quad[] | null {
  const t = BLOCKS[id].tiles;
  const a = U_A[id], b = U_B[id];
  switch (kind) {
    case UK.CHEST:
      return chestQuads(CH_FACING[id], CH_OPEN[id] === 1, ENTITY_TILE_BASE, CHEST_MODELS[CH_KIND[id]][CH_TYPE[id]]);
    case UK.ENDER:
      return chestQuads(a, false, ENTITY_TILE_BASE, ENDER_CHEST_MODEL);
    case UK.HOPPER: {
      // block/hopper.json: a hollow 16x6 funnel top, the 8x6 middle, and the 4x4 spout
      const out = T.HOPPER_OUTSIDE, top = [out, out, T.HOPPER_TOP, out, out, out];
      const quads = [
        ...box([0, 10, 0, 16, 11, 16], [out, out, T.HOPPER_INSIDE, out, out, out]),
        ...box([0, 11, 0, 2, 16, 16], top), ...box([14, 11, 0, 16, 16, 16], top),
        ...box([2, 11, 0, 14, 16, 2], top), ...box([2, 11, 14, 14, 16, 16], top),
        ...box([4, 4, 4, 12, 10, 12], all(out)),
      ];
      const spout: Record<number, number[]> = {
        3: [6, 0, 6, 10, 4, 10], 0: [12, 4, 6, 16, 8, 10], 1: [0, 4, 6, 4, 8, 10], 4: [6, 4, 12, 10, 8, 16], 5: [6, 4, 0, 10, 8, 4],
      };
      quads.push(...box(spout[a], all(out)));
      return quads;
    }
    case UK.SIGN: {
      return signQuads(a, (b * Math.PI) / 8, false, ENTITY_TILE_BASE);
    }
    case UK.WALL_SIGN:
      return signQuads(a, FACING_ANGLE[b], true, ENTITY_TILE_BASE);
    case UK.SHULKER:
      return shulkerBoxQuads(ENTITY_TILE_BASE);
    case UK.COMPOSTER: {
      const quads: Quad[] = [];
      for (const r of TUB) quads.push(...box(r, t));
      if (a > 0) quads.push(...box([2, 2, 2, 14, Math.min(15, 1 + a * 2), 14], all(a >= 8 ? T.COMPOSTER_READY : T.COMPOSTER_COMPOST)));
      return quads;
    }
    case UK.CAULDRON: {
      const quads: Quad[] = [];
      for (const r of TUB) quads.push(...box(r, [t[0], t[1], r[4] === 2 ? T.CAULDRON_INNER : t[2], t[3], t[4], t[5]]));
      if (a === 1) quads.push(...box([2, 2, 2, 14, 6 + b * 3, 14], all(T.WATER)));
      else if (a === 2) quads.push(...box([2, 2, 2, 14, 15, 14], all(T.LAVA)));
      return quads;
    }
    case UK.CAKE:
      return box([1 + a * 2, 0, 1, 15, 8, 15], [t[0], a > 0 ? T.CAKE_INNER : t[1], t[2], t[3], t[4], t[5]]);
    case UK.FRAME:
      return [...box(attachedBox(a, 12, 12, 1), t)];
    case UK.POT: {
      const quads = box([5, 0, 5, 11, 6, 11], [t[0], t[1], a ? T.DIRT : t[0], t[3], t[4], t[5]]);
      if (a) quads.push(...crossQuads(t[2], 4, 16, 6));
      return quads;
    }
    case UK.LADDER:
      return box(attachedBox(a, 16, 16, 1), t);
    case UK.VINE:
      return box(attachedBox(a, 16, 16, 0.5), t);
    case UK.CHAIN: {
      // block/chain.json: two crossed 3-wide planes, uv 0..3 and 3..6
      const up = [...turn(plane(t[0], 6.5, 0, 9.5, 16, [0, 0, 3, 16]), Math.PI / 4), ...turn(plane(t[0], 6.5, 0, 9.5, 16, [3, 0, 6, 16]), -Math.PI / 4)];
      if (a === 1) return up;
      // lying along X or Z: stand the vertical chain on its side
      return up.map((q) => {
        const pts: number[][] = [];
        for (let i = 0; i < 4; i++) {
          const x = q.pos[i * 3], y = q.pos[i * 3 + 1], z = q.pos[i * 3 + 2];
          pts.push(a === 0 ? [y, x, z] : [x, z, y]);
        }
        const uvs = [0, 1, 2, 3].map((i) => [q.uv[i * 2], q.uv[i * 2 + 1]]);
        const n = cross(pts);
        return orientedQuad(pts, uvs, q.tile, n);
      });
    }
    case UK.LANTERN: {
      // block/template_lantern.json / template_hanging_lantern.json
      const y = b ? 1 : 0;
      const quads = [
        ...ubox([5, y, 5, 11, y + 7, 11], t, sides([0, 2, 6, 9], [0, 9, 6, 15])),
        ...ubox([6, y + 7, 6, 10, y + 9, 10], t, sides([1, 0, 5, 2], [1, 10, 5, 14])),
      ];
      if (b) {
        quads.push(...turn(plane(t[0], 6.5, 11, 9.5, 15, [11, 1, 14, 5]), Math.PI / 4), ...turn(plane(t[0], 6.5, 10, 9.5, 16, [11, 6, 14, 12]), -Math.PI / 4));
      } else {
        quads.push(...turn(plane(t[0], 6.5, 9, 9.5, 11, [11, 10, 14, 12]), Math.PI / 4), ...turn(plane(t[0], 6.5, 9, 9.5, 11, [11, 10, 14, 12]), -Math.PI / 4));
      }
      return quads;
    }
    case UK.CANDLE: {
      // block/template_candle.json and its two / three / four variants: [x, z, height]
      const sets = [[[7, 7, 6]], [[5, 7, 6], [9, 6, 5]], [[8, 6, 6], [5, 7, 5], [7, 9, 3]], [[8, 5, 6], [5, 5, 5], [9, 8, 5], [6, 8, 3]]];
      const quads: Quad[] = [];
      for (const [x, z, h] of sets[Math.max(1, Math.min(4, a)) - 1]) {
        quads.push(...ubox([x, 0, z, x + 2, h, z + 2], t, [[0, 8, 2, 8 + h], [0, 8, 2, 8 + h], [0, 6, 2, 8], [0, 14, 2, 16], [0, 8, 2, 8 + h], [0, 8, 2, 8 + h]]));
        quads.push(...turn(plane(t[0], x + 0.5, h, x + 1.5, h + 1, [0, 5, 1, 6]).map((q) => shift(q, 0, 0, (z + 1 - 8) / 16)), 0));
      }
      return quads;
    }
    case UK.CAMPFIRE: {
      // block/template_campfire.json: two logs on the ground, two across them, the ash bed, the crossed flame
      const long = [0, 0, 16, 4], end = [0, 4, 4, 8];
      const quads = [
        ...ubox([1, 0, 0, 5, 4, 16], t, [long, long, long, long, end, end]), ...ubox([11, 0, 0, 15, 4, 16], t, [long, long, long, long, end, end]),
        ...ubox([0, 3, 1, 16, 7, 5], t, [end, end, long, long, long, long]), ...ubox([0, 3, 11, 16, 7, 15], t, [end, end, long, long, long, long]),
        ...ubox([5, 0, 0, 11, 1, 16], t, [[0, 15, 16, 16], [0, 15, 16, 16], [0, 8, 16, 14], [0, 8, 16, 14], [0, 15, 6, 16], [10, 15, 16, 16]]),
      ];
      if (a) quads.push(...turn(plane(T.CAMPFIRE_FIRE, 0.8, 1, 15.2, 17, [0, 0, 16, 16]), Math.PI / 4), ...turn(plane(T.CAMPFIRE_FIRE, 0.8, 1, 15.2, 17, [0, 0, 16, 16]), -Math.PI / 4));
      return quads;
    }
    case UK.ROD:
      // block/lightning_rod.json
      return [...ubox([7, 0, 7, 9, 12, 9], t, sides([0, 4, 2, 16], [0, 4, 2, 6])), ...ubox([6, 12, 6, 10, 16, 10], t, sides([0, 0, 4, 4], [0, 0, 4, 4]))];
    default:
      return null; // dispenser, dropper: plain cubes
  }
}
